import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  List,
  Space,
  Typography,
  Badge,
  Tooltip,
  Card,
  Button,
} from "antd";
import { MessageOutlined, SmileOutlined } from "@ant-design/icons";
import CommentDrawer from "./CommentDrawer";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getClientid, getUserInfo, getUserList } from "redux/selectors";
import { STATUS_CODE, WEB_SOCKET } from "common/Constants";

const { Title, Text } = Typography;

const Wrapper = styled.div`
  .ant-card {
    box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
    border-radius: 5px;
  }
  .ant-list {
    height: 200px;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .ant-list-item-meta {
    display: flex;
    flex: 1 1;
    align-items: center;
    max-width: 100%;
  }
  .ant-list-item-action {
    flex: 0 0 auto;
    margin-right: 20px;
    padding: 0;
    font-size: 0;
    list-style: none;
  }
`;

const emojiMap = {
  1: "😊",
  2: "😍",
  3: "🎉",
  4: "😄",
  5: "❤️",
};

const emojis = Object.entries(emojiMap).map(([id, emoji]) => ({ id, emoji }));

const EmojiTooltip = ({ onSelect }) => (
  <Space size={8}>
    {emojis.map(({ id, emoji }) => (
      <span
        key={id}
        style={{ fontSize: 18, cursor: "pointer", lineHeight: 1 }}
        onClick={() => onSelect(id)}
      >
        {emoji}
      </span>
    ))}
  </Space>
);

export default function BirthdaySpotLight() {
  const { t } = useTranslation();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const isUnmounted = useRef(false);
  const drawerMessageHandlerRef = useRef(null);
  const openDrawerRef = useRef(false); // ✅ track drawer open state
  const selectedUserRef = useRef(null); // ✅ track selected user

  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const clientId = useSelector(getClientid);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [birthdayData, setBirthdayData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Keep refs in sync with state ─────────────────────────────────────────
  useEffect(() => {
    openDrawerRef.current = openDrawer;
  }, [openDrawer]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // ── Fetch birthday list ──────────────────────────────────────────────────
  const fetchBirthdays = async () => {
    try {
      setLoading(true);
      const { code, birthdayList } = await CALL_API(`chat`, "post", {
        action: "getBirthdayList",
        clientId,
      });

      if (code === STATUS_CODE.SUCCESS) {
        const formatted = (birthdayList || []).map((item) => ({
          id: item.employeeid,
          userid: item.userid,
          name: item.fullname,
          role: item.designation || item.jobtitle || item.employeeid,
          avatar:
            item.profileurl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              item.fullname
            )}`,
          reactions: (item.reactions || []).map((r) => ({
            emoji: r.emoji || r.reaction,
            count: parseInt(r.count || 1, 10),
          })),
          messageCount: parseInt(item.messageCount || 0, 10),
          employeeId: item.employeeid,
        }));
        setBirthdayData(formatted);
      }
    } catch (err) {
      console.error("Birthday API error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── WebSocket ────────────────────────────────────────────────────────────
  const connectWebSocket = () => {
    if (isUnmounted.current) return;

    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    console.log("🔌 Connecting WebSocket...");
    const ws = new WebSocket(WEB_SOCKET);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("✅ WebSocket connected");
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        console.log("📩 WS Payload:", payload.type, payload);

        // ── NEW_MESSAGE ──
        if (payload.type === "NEW_MESSAGE") {
          const empId = payload.data?.employee_id;

          // ✅ Always update message count on birthday card
          setBirthdayData((prev) =>
            prev.map((b) =>
              b.employeeId === empId
                ? {
                    ...b,
                    messageCount: payload.messageCount ?? b.messageCount + 1,
                  }
                : b
            )
          );

          // ✅ Forward to drawer only if drawer is open AND same employee
          if (
            openDrawerRef.current &&
            selectedUserRef.current?.employeeId === empId &&
            drawerMessageHandlerRef.current
          ) {
            console.log("✅ Forwarding NEW_MESSAGE to drawer");
            drawerMessageHandlerRef.current(payload);
          } else {
            console.log("⚠️ Drawer not open or wrong employee:", {
              drawerOpen: openDrawerRef.current,
              selectedEmp: selectedUserRef.current?.employeeId,
              payloadEmp: empId,
              handlerExists: !!drawerMessageHandlerRef.current,
            });
          }
        }

        // ── NEW_REPLY ──
        if (payload.type === "NEW_REPLY") {
          if (openDrawerRef.current && drawerMessageHandlerRef.current) {
            console.log("✅ Forwarding NEW_REPLY to drawer");
            drawerMessageHandlerRef.current(payload);
          }
        }

        // ── REACTION_UPDATE ──
        if (payload.type === "REACTION_UPDATE") {
          const normalized = (payload.groupedReactions || []).map((r) => ({
            emoji: r.emoji || r.reaction,
            count: parseInt(r.count || 1, 10),
          }));

          if (payload.messageId) {
            // Message reaction → forward to drawer
            if (openDrawerRef.current && drawerMessageHandlerRef.current) {
              drawerMessageHandlerRef.current({
                ...payload,
                groupedReactions: normalized,
              });
            }
          } else if (payload.employeeId) {
            // Spotlight reaction → update birthday card
            setBirthdayData((prev) =>
              prev.map((b) =>
                b.employeeId === payload.employeeId
                  ? { ...b, reactions: normalized }
                  : b
              )
            );
          }
        }
      } catch (err) {
        console.error("❌ WS parse error:", err);
      }
    };

    ws.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
    };

    ws.onclose = (e) => {
      if (isUnmounted.current) return;
      console.warn(`🔌 WS closed (code: ${e.code}). Reconnecting in 3s...`);
      reconnectTimer.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };
  };

  useEffect(() => {
    isUnmounted.current = false;
    connectWebSocket();

    return () => {
      isUnmounted.current = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    fetchBirthdays();
  }, []);

  // ── Open comment drawer ──────────────────────────────────────────────────
  const openComments = (user) => {
    setSelectedUser(user);
    setOpenDrawer(true);
  };

  // ── Send spotlight reaction ──────────────────────────────────────────────
  const sendReaction = async (emojiId, item) => {
    const reaction = emojiMap[emojiId];
    if (!reaction) return;

    // Optimistic update
    setBirthdayData((prev) =>
      prev.map((b) => {
        if (b.id !== item.id) return b;
        const existing = (b.reactions || []).find(
          (r) => (r.emoji || r.reaction) === reaction
        );
        const updatedReactions = existing
          ? b.reactions.map((r) =>
              (r.emoji || r.reaction) === reaction
                ? { emoji: reaction, count: parseInt(r.count || 1, 10) + 1 }
                : {
                    emoji: r.emoji || r.reaction,
                    count: parseInt(r.count || 1, 10),
                  }
            )
          : [
              ...b.reactions.map((r) => ({
                emoji: r.emoji || r.reaction,
                count: parseInt(r.count || 1, 10),
              })),
              { emoji: reaction, count: 1 },
            ];
        return { ...b, reactions: updatedReactions };
      })
    );

    try {
      const { code, groupedReactions } = await CALL_API(`chat`, "post", {
        action: "reactMessage",
        employeeId: item.employeeId, // ✅ birthday person
        userId: userInfo?.userid, // ✅ logged-in user
        reaction,
      });

      if (code === STATUS_CODE.SUCCESS && groupedReactions) {
        const normalized = groupedReactions.map((r) => ({
          emoji: r.emoji || r.reaction,
          count: parseInt(r.count || 1, 10),
        }));
        setBirthdayData((prev) =>
          prev.map((b) =>
            b.id === item.id ? { ...b, reactions: normalized } : b
          )
        );
      }
    } catch (err) {
      console.error("Reaction error:", err);
      fetchBirthdays();
    }
  };

  return (
    <Wrapper>
      <Card>
        <div className="header">
          <Title level={5}>
            <Text strong>{t("Today's Birthday Spotlight")}</Text>
          </Title>
        </div>

        <List
          loading={loading}
          dataSource={birthdayData}
          itemLayout="horizontal"
          style={{ maxHeight: 350, overflowY: "auto", marginTop: 10 }}
          renderItem={(item) => (
            <List.Item
              actions={[
                // Emoji reaction picker
                <Tooltip
                  key="emoji"
                  trigger="click"
                  placement="top"
                  title={
                    <EmojiTooltip onSelect={(id) => sendReaction(id, item)} />
                  }
                >
                  <SmileOutlined
                    style={{
                      fontSize: 18,
                      cursor: "pointer",
                      color: "#2D53DA",
                    }}
                  />
                </Tooltip>,

                // Message count + icon
                <Space key="comment" size={4} align="center">
                  <Text style={{ fontSize: 12 }}>{item.messageCount}</Text>
                  <MessageOutlined
                    style={{
                      fontSize: 18,
                      cursor: "pointer",
                      color: "#2D53DA",
                    }}
                    onClick={() => openComments(item)}
                  />
                </Space>,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar src={item.avatar} size={48} />}
                title={<Text strong>{item.name}</Text>}
                description={
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">{item.role}</Text>
                    {item.reactions.length > 0 && (
                      <Space wrap size={4}>
                        {item.reactions.map((r, i) => (
                          <Button
                            key={i}
                            size="small"
                            style={{
                              borderRadius: 12,
                              padding: "0 8px",
                              fontSize: 12,
                              height: 24,
                            }}
                          >
                            {r.emoji} {r.count}
                          </Button>
                        ))}
                      </Space>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />

        <CommentDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          user={selectedUser}
          setBirthdayData={setBirthdayData}
          drawerMessageHandlerRef={drawerMessageHandlerRef}
          userInfo={userInfo}
        />
      </Card>
    </Wrapper>
  );
}
